import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import ChevronRight from '@atlaskit/icon/glyph/chevron-right';
import Button from '@atlaskit/button';
import './custom-panel.scss';

export interface CustomPanelProps {
  isSelected?: boolean;
  isCustomPanelV2Enabled?: boolean;
  isDefaultExpanded?: boolean;
  toggleButtonOnLeft?: boolean;
  noHoverBackground?: boolean;
  toggleButtonPrefix?: React.ReactNode;
  toggleButtonSuffix?: React.ReactNode;
  heading?: React.ReactNode;
  onChange?: (isExpanded: boolean) => void;
  children?: React.ReactNode;
  testId?: string;
  className?: string;
  isCustomExpanded?: boolean;
  iconBefore?: React.ReactNode;
  customToggleTestId?: string;
  checkboxPrefix?: React.ReactNode;
}

export const CustomPanel = ({
  heading,
  onChange,
  children,
  testId = 'custom-panel',
  isSelected = false,
  isCustomPanelV2Enabled = false,
  isDefaultExpanded = false,
  toggleButtonOnLeft = false,
  toggleButtonPrefix,
  toggleButtonSuffix,
  noHoverBackground = false,
  className = '',
  isCustomExpanded, // needed this prop to update the expanded state after the initial render
  iconBefore,
  customToggleTestId = 'custom-panel-toggle-button',
  checkboxPrefix,
}: CustomPanelProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(isDefaultExpanded);
  const onIconClick = () => {
    onChange && onChange(!isExpanded);
    setIsExpanded(!isExpanded);
  };

  // this effect is to update the expanded state after the initial render
  useEffect(() => {
    if (isCustomExpanded !== undefined && isExpanded !== isCustomExpanded) {
      setIsExpanded(isCustomExpanded);
    }
  }, [isCustomExpanded]);

  const toggleButton = (
    <div className="flex align-center">
      {toggleButtonPrefix}
      {checkboxPrefix}
      <Button
        iconBefore={
          <div className={classnames('custom-panel-icon', { expanded: isExpanded })}>
            <ChevronRight size="medium" label="expand-collapse" />
          </div>
        }
        appearance="subtle"
        onClick={onIconClick}
        testId={customToggleTestId}
      />
      {toggleButtonSuffix}
    </div>
  );

  return (
    <div
      className={classnames('ppc-custom-panel', className, {
        'ppc-custom-panel-v2': isCustomPanelV2Enabled,
        'ppc-custom-panel--no-hover': noHoverBackground,
        'ppc-custom-panel--selected': isSelected,
        'ppc-custom-panel--expanded': isExpanded,
      })}
      data-testid={testId}
    >
      <div
        className={classnames('ppc-custom-panel-header flex align-center', {
          'justify-space-between': !toggleButtonOnLeft,
          'pv-4 ph-8': isCustomPanelV2Enabled,
        })}
      >
        {toggleButtonOnLeft ? (
          <>
            {toggleButton}
            {iconBefore && <div className="custom-panel-before-icon">{iconBefore}</div>}
            {heading}
          </>
        ) : (
          <>
            {heading}
            {toggleButton}
          </>
        )}
      </div>
      <div className={classnames('custom-panel-container', { expanded: isExpanded })}>{children}</div>
    </div>
  );
};
